import { ConnectedPosition, Overlay, OverlayPositionBuilder, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  ComponentRef,
  Directive,
  ElementRef,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  inject,
} from '@angular/core';
import { TooltipComponent } from './tooltip.component';

type TooltipPosition = 'left' | 'right' | 'above' | 'below';

function getPositions(position: TooltipPosition, offset: number): ConnectedPosition[] {
  switch (position) {
    case 'above':
      return [
        {
          originX: 'center',
          originY: 'top',
          overlayX: 'center',
          overlayY: 'bottom',
          offsetY: -offset,
          panelClass: 'above',
        },
        {
          originX: 'center',
          originY: 'bottom',
          overlayX: 'center',
          overlayY: 'top',
          offsetY: offset,
          panelClass: 'below',
        },
      ];
    case 'below':
      return [
        {
          originX: 'center',
          originY: 'bottom',
          overlayX: 'center',
          overlayY: 'top',
          offsetY: -offset,
          panelClass: 'below',
        },
        {
          originX: 'center',
          originY: 'top',
          overlayX: 'center',
          overlayY: 'bottom',
          offsetY: -offset,
          panelClass: 'above',
        },
      ];
    case 'left':
      return [
        {
          originX: 'start',
          originY: 'center',
          overlayX: 'end',
          overlayY: 'center',
          offsetX: -offset,
          panelClass: 'left',
        },
        {
          originX: 'end',
          originY: 'center',
          overlayX: 'start',
          overlayY: 'center',
          offsetX: offset,
          panelClass: 'right',
        },
      ];
    case 'right':
      return [
        {
          originX: 'end',
          originY: 'center',
          overlayX: 'start',
          overlayY: 'center',
          offsetX: offset,
          panelClass: 'right',
        },
        {
          originX: 'start',
          originY: 'center',
          overlayX: 'end',
          overlayY: 'center',
          offsetX: -offset,
          panelClass: 'left',
        },
      ];
  }
}

@Directive({
  selector: '[cslTooltip]',
  standalone: true,
  host: {
    '[attr.tabindex]': 'isFocusable ? 0 : null',
    '[attr.aria-label]': 'text',
    '(keydown.esc)': 'hide()',
    '(mouseenter)': 'show()',
    '(focusin)': 'show()',
    '(mouseleave)': 'hide()',
    '(focusout)': 'hide()',
  },
})
export class TooltipDirective implements OnInit, OnDestroy {
  private overlay = inject(Overlay);
  private overlayPositionBuilder = inject(OverlayPositionBuilder);
  private elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  private overlayRef!: OverlayRef;
  private isOverflowOnly = false;
  // Offset should be synced with offset in css 'tooltip.component.css'
  private readonly offset = 10;

  @Input('cslTooltip')
  get text(): string | TemplateRef<unknown> | null {
    return this.tooltipText;
  }

  set text(value: string | TemplateRef<unknown> | null) {
    this.tooltipText = value;
  }

  private tooltipText: string | TemplateRef<unknown> | null = '';

  @Input()
  position: TooltipPosition = 'above';

  @Input()
  tooltipClass?: string;

  @Input()
  get onOverflowOnly(): boolean {
    return this.isOverflowOnly;
  }

  set onOverflowOnly(value: boolean) {
    this.isOverflowOnly = value;
  }

  @Input() tooltipFocusable = true;

  private tooltipRef?: ComponentRef<TooltipComponent>;

  protected get isFocusable() {
    return this.tooltipFocusable && (!this.onOverflowOnly || !this.isElementNotOverflown());
  }

  ngOnInit(): void {
    const positionStrategy = this.overlayPositionBuilder
      .flexibleConnectedTo(this.elementRef)
      .withPositions(getPositions(this.position, this.offset));

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.close(),
    });
  }

  show(): void {
    const text = this.text ?? this.elementRef.nativeElement.textContent?.trim();
    if (!text || this.isNotOverflowCase() || this.overlayRef.hasAttached()) return;

    this.tooltipRef = this.overlayRef.attach(new ComponentPortal(TooltipComponent));
    this.tooltipRef.instance.text = text;
    this.tooltipRef.instance.className = this.tooltipClass || '';
  }

  hide(): void {
    if (this.overlayRef.hasAttached()) {
      this.overlayRef.detach();
    }
  }

  @HostBinding('class') get hostClass(): string {
    return this.onOverflowOnly ? 'overflow-hidden text-ellipsis text-nowrap' : '';
  }

  private isElementNotOverflown(): boolean {
    const hostElem = this.elementRef.nativeElement;

    return hostElem.scrollWidth <= hostElem.clientWidth;
  }

  private isNotOverflowCase(): boolean {
    return this.onOverflowOnly && this.isElementNotOverflown();
  }

  ngOnDestroy(): void {
    this.overlayRef.dispose();
    this.tooltipRef?.destroy();
  }
}
